<script>
  // Search page, refactored onto the shared CardCollection shell (ADR-0010).
  // The shell owns the search tools (query/filters/sort), list/grid views,
  // skeletons, empty state, and NUMBERED pagination. This page supplies the
  // search dataSource (the search_cards API behind a cursor→pages pager), the
  // printing→card-item adapter, and the card-detail popup via onCardClick.
  import CardCollection from '../cardcollection/CardCollection.svelte';
  import SearchCardDetail from './SearchCardDetail.svelte';
  import { getRecentBinders } from '../../lib/services/recent-binders.js';
  import { buildBinderKeys } from '../../lib/logic/url-parser.js';
  import {
    buildApiFilters,
    filtersFromParams,
    filtersToParams,
    sortModeToSortBy,
  } from '../../lib/logic/search-logic.js';
  import { printingToItem } from '../../lib/logic/card-collection-logic.js';
  import { createCursorPager } from '../../lib/logic/cursor-pager.js';
  import { getCurrencySymbol } from '../../lib/logic/utils.js';
  import { SEARCH_RESULTS_PER_PAGE } from '../../lib/logic/config.js';
  import { searchCards } from '../../lib/services/search-api.js';
  import { groupsState } from '../../lib/stores/groups.svelte.js';
  import { getGroupDetail } from '../../lib/services/groups-api.js';
  import { route, navigate } from '../../lib/stores/router.svelte.js';
  import {
    browseState,
    activeSubgroupIds,
    groupUrlParams,
    selectedServerGroupId,
    syncSelectionFromRoute,
  } from '../../lib/stores/browse.svelte.js';
  import {
    authState,
    isUserAnonymous,
    initiateDiscordOAuth,
  } from '../../lib/stores/auth.svelte.js';

  // --- Initialize state from URL params ---
  let currentQuery = $state(route.params.q || '');
  let viewMode = $state(route.params.view || 'list');
  let sortMode = $state(route.params.sort || 'name-asc');
  let initialFilters = $state(filtersFromParams(route.params));

  // Sync URL group/sub params into browseState on mount
  syncSelectionFromRoute(route.params);

  const subgroupIds = $derived(activeSubgroupIds());

  // --- Card detail overlay ---
  let detailPrinting = $state(null);

  const currency = 'usd';
  const currencySymbol = $derived(getCurrencySymbol(currency));

  let emptyMessage = $state('Type a card name to search across your binders');
  let groupInfo = $state(null);

  // Scope-aware query placeholder: a selected group → "all binders in <group>",
  // otherwise the cross-binder default.
  const inGroup = $derived(selectedServerGroupId() !== null);
  const searchPlaceholder = $derived(
    inGroup
      ? `Search all binders in ${groupInfo?.name || 'this group'}...`
      : 'Search across all binders...'
  );

  // Fetch group info when a group is selected via URL param.
  $effect(() => {
    if (!route.params.group || !authState.ready || isUserAnonymous()) return;
    const local = groupsState.userGroups.find((g) => g.groupId === route.params.group);
    if (local) {
      groupInfo = { name: local.name, iconUrl: local.iconUrl || null };
      emptyMessage = `Type a card name to search across ${local.name}'s binders`;
    } else {
      getGroupDetail(route.params.group)
        .then(({ group }) => {
          if (group) {
            groupInfo = { name: group.name, iconUrl: group.iconUrl || null };
            emptyMessage = `Type a card name to search across ${group.name}'s binders`;
          }
        })
        .catch(() => {});
    }
  });

  // --- Build request body for one search page ---
  async function buildRequestBody(query, filters) {
    const apiFilters = buildApiFilters(filters);
    const body = {
      currency,
      limit: SEARCH_RESULTS_PER_PAGE,
      sort_by: sortModeToSortBy(sortMode),
    };
    if (query) body.query = query;
    body.filters = apiFilters || { browse_all: true };

    const gid = selectedServerGroupId();
    if (gid) {
      body.groupId = gid;
      if (subgroupIds.length > 0) body.subgroupIds = subgroupIds;
    } else {
      const recentBinders = await getRecentBinders();
      const binderKeys = buildBinderKeys(recentBinders);
      if (binderKeys.length === 0) return null; // no binders to search
      body.binderKeys = binderKeys;
    }
    return body;
  }

  // One backend fetch for a given cursor (the cursor-pager's only coupling).
  async function fetchSearchPage(query, filters, cursor) {
    const body = await buildRequestBody(query, filters);
    if (!body) return { items: [], total: 0, next_cursor: null };
    if (cursor) body.cursor = cursor;

    const data = await searchCards(body);
    if (!data.success) return { items: [], total: 0, next_cursor: null };

    const items = (data.results || []).map(printingToItem);
    return {
      items,
      total: data.total_count ?? items.length,
      next_cursor: data.next_cursor || null,
    };
  }

  // The shell's dataSource. A new (query, filters, sort, group) resets the cursor
  // pager so page 1 starts a fresh walk; same key → revisits are cache hits. The
  // pager only steps next/prev (a cursor backend can't random-access), so we walk
  // it toward the shell's requested page one step at a time (review §8). The
  // `group` is part of the pager key, so a group switch starts a fresh pager
  // without remounting the shell — the user's view/sort survive (no {#key}).
  let pager = null;
  let pagerKey = '';

  const dataSource = async (query, filters, sort, page) => {
    const key = JSON.stringify({
      query,
      filters,
      sort,
      group: browseState.selectedGroupId,
      sub: subgroupIds,
    });
    if (key !== pagerKey || !pager) {
      pagerKey = key;
      pager = createCursorPager((cursor) => fetchSearchPage(query, filters, cursor));
    }
    let result = await pager.current_();
    while (pager.currentPage() < page && pager.hasNext()) {
      result = await pager.next();
    }
    while (pager.currentPage() > page) {
      result = await pager.prev();
    }
    return result;
  };

  function openCardDetail(item) {
    // The popup expects the rich printing payload; the card-item carries it
    // through imageRef (the full Scryfall card object from the search API).
    detailPrinting = item.imageRef || item;
  }
  function closeCardDetail() {
    detailPrinting = null;
  }

  function handleStateChange({ query, filters, sort, view }) {
    currentQuery = query;
    sortMode = sort;
    viewMode = view;
    const params = { ...filtersToParams(filters) };
    if (query) params.q = query;
    Object.assign(params, groupUrlParams());
    if (sort && sort !== 'name-asc') params.sort = sort;
    if (view && view !== 'list') params.view = view;
    navigate('search', params, true);
  }
</script>

{#if route.params.group && authState.ready && isUserAnonymous()}
  <div class="search-auth-gate">
    <div class="search-auth-card">
      <svg
        viewBox="0 0 24 24"
        width="48"
        height="48"
        fill="none"
        stroke="currentColor"
        stroke-width="1.5"
        opacity="0.4"
      >
        <circle cx="11" cy="11" r="8"></circle>
        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
      </svg>
      <h2>Sign in to search this group</h2>
      <p>Sign in with Discord to search cards across this group's binders.</p>
      <button class="btn-discord" onclick={() => initiateDiscordOAuth()}>
        <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
          <path
            d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"
          />
        </svg>
        Sign in with Discord
      </button>
    </div>
  </div>
{:else}
  <div class="search-page">
    <CardCollection
      {dataSource}
      {currencySymbol}
      initialQuery={currentQuery}
      initialSort={sortMode}
      defaultView={viewMode}
      {initialFilters}
      emptyCopy={emptyMessage}
      {searchPlaceholder}
      resetKey={browseState.selectedGroupId + ':' + subgroupIds.join(',')}
      autofocus
      onCardClick={openCardDetail}
      onStateChange={handleStateChange}
    />
  </div>

  {#if detailPrinting}
    <SearchCardDetail printing={detailPrinting} {currencySymbol} onClose={closeCardDetail} />
  {/if}
{/if}

<style>
  .search-auth-gate {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px 16px;
  }
  .search-auth-card {
    background: white;
    border-radius: 20px;
    padding: 40px 32px;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.1);
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    max-width: 400px;
  }
  .search-auth-card h2 {
    font-size: 1.5rem;
    font-weight: 700;
    color: #1f2937;
    margin: 0;
  }
  .search-auth-card p {
    font-size: 15px;
    color: #6b7280;
    margin: 0;
    line-height: 1.5;
  }
  .btn-discord {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 12px 24px;
    background: #5865f2;
    color: white;
    border: none;
    border-radius: 10px;
    font-size: 15px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.2s;
    font-family: inherit;
  }
  .btn-discord:hover {
    background: #4752c4;
  }

  .search-page {
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    background: var(--color-bg);
    width: 100%;
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 var(--space-xl);
  }

  @media (max-width: 768px) {
    .search-page {
      padding: 0 var(--space-md);
    }
  }
</style>
